import React from 'react';
import styled from 'styled-components';
import { Grid } from '../../../app/StyleGuide';
import { FileUploader, UploadedFile } from '../../../components/fileUploader';
import type { FileUploaderProps } from '../../../components/fileUploader/models';
import alertService from '../../../services/AlertService';
import { handleError } from '../../../utils/apiUtils';
import { getFileType } from '../../../utils/fileUtils';
import { User } from '../../authentication';
import { saveVideo } from '../api/saveVideo';
import { uploadContent } from '../api/uploadContent';
import { Content } from '../models/content';
import { StepType } from '../models/stepType';

const UploadViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 400px;
  margin-bottom: ${Grid._4};

  p {
    margin: auto;
  }
`;

type ContentUploadStepProps = {
  stepType: StepType;
  selectedContents: Content[];
  setSelectedContents: (contents: Content[]) => void;
  setContents: React.Dispatch<React.SetStateAction<{ loading: boolean; data?: Content[]; error?: boolean }>>;
  user: User | undefined;
  ownerId?: string;
  onUploadComplete: (content: Content) => void;
};

const ACCEPTS_BY_STEP_TYPE: Partial<Record<StepType, NonNullable<FileUploaderProps['accepts']>>> = {
  video: ['video'],
  audio: ['audio'],
  documentViewer: ['pdf'],
};

export const ContentUploadStep = ({
  stepType,
  selectedContents,
  setSelectedContents,
  setContents,
  user,
  ownerId,
  onUploadComplete,
}: ContentUploadStepProps) => {
  const determineUploadLocationByFile = (fileToUpload: File) => {
    switch (getFileType(fileToUpload.type)) {
      case 'audio':
      case 'video':
        return 'v1/storage/videos';
      default:
        return 'v1/storage/files';
    }
  };

  const handleFileUploaded = (uploadedFile: UploadedFile) => {
    if (!user?.lastSelectedAccount || !user?.userId) {
      return;
    }

    const fileType = getFileType(uploadedFile.type);
    const orgId = ownerId || user.lastSelectedAccount;
    const userId = user.userId;

    if (fileType === 'video' || fileType === 'audio') {
      saveVideo(uploadedFile.id, uploadedFile.name, orgId, userId)
        .then(res => {
          const videoContentItem: Content = {
            id: res.id,
            name: res.title,
            type: 'Video',
            lastModifiedDate: new Date().toISOString(),
            published: false,
          };
          const newSelectedContents = [...selectedContents, videoContentItem];
          setSelectedContents(newSelectedContents);
          alertService.show('Media Added');
          onUploadComplete(videoContentItem);

          setContents(prevContents => ({
            ...prevContents,
            data: prevContents.data ? [...prevContents.data, videoContentItem] : [videoContentItem],
          }));
        })
        .catch(handleError);
    } else {
      uploadContent(uploadedFile.id, uploadedFile.name, orgId, userId)
        .then(res => {
          const downloadContentItem: Content = {
            id: res.id,
            name: res.fileName,
            type: 'Download',
            lastModifiedDate: new Date().toISOString(),
            published: true,
          };
          const newSelectedContents = [...selectedContents, downloadContentItem];
          setSelectedContents(newSelectedContents);
          alertService.show('Media Added');
          onUploadComplete(downloadContentItem);

          setContents(prevContents => ({
            ...prevContents,
            data: prevContents.data ? [...prevContents.data, downloadContentItem] : [downloadContentItem],
          }));
        })
        .catch(handleError);
    }
  };

  return (
    <UploadViewContainer>
      <FileUploader
        onFileUploaded={handleFileUploaded}
        onFileRemoved={() => {}}
        uploadLocation={determineUploadLocationByFile}
        orgId={user?.lastSelectedAccount}
        accepts={ACCEPTS_BY_STEP_TYPE[stepType]}
      />
    </UploadViewContainer>
  );
};
